<script setup lang="ts">
import DashboardLayout from '@/onenetwork/layouts/DashboardLayout.vue'
import DataTable from '@/onenetwork/components/datatable/DataTable.vue'
import AllowPerm from '@/onenetwork/components/perms/AllowPerm.vue'
import { Icon } from '@iconify/vue'
import { computed, onMounted, watch } from 'vue'
import { useReactiveQueryParam, useUpdateUrlParam } from '@/composables/useReactiveQueryParam'
import _ from 'lodash'
import { useRouter } from 'vue-router'
import {
  EstateProjectListActionTypes,
  useEstateProjectListStore,
} from '@/modules/estate-project/stores/estate-project-list-store'
import EstateProjectDetail from '@/modules/estate-project/views/estate-project-detail.vue'

const router = useRouter()
const store = useEstateProjectListStore()

const loading = computed(() => store.action_type === EstateProjectListActionTypes.FETCHING)
const tableConfig = computed(() => store.tableConfig)
const dataSource = computed(() => store.data)
const page = computed(() => store.page)
const pageSize = computed(() => store.pageSize)
const pageTabs = computed(() => store.tabs)
const displayTabContent = computed(() => store.tabs.find((tab) => tab.active)?.id || 'list')
const detailId: any = useReactiveQueryParam('detailId')
const { updateUrlParam } = useUpdateUrlParam()

const activeTab = computed(() => {
  return store.tabs.find((tab) => tab.active)
})

const syncDetailQuery = () => {
  const id = activeTab.value?.id
  const queryValue = id && id !== 'list' ? String(id) : null
  updateUrlParam('detailId', queryValue)
}

const handleChangeTab = (tab: { id: string | number }) => {
  store.changeTab(tab.id)
  syncDetailQuery()
}

const handleCloseTab = (tab: { id: string | number }) => {
  store.closeTab(tab.id)
  syncDetailQuery()
}

const handleCloseAllTab = () => {
  store.closeAllTab()
  syncDetailQuery()
}

const handleOpenDetailTab = (item: { id: number; project_name?: string }) => {
  store.newTab({
    id: item.id,
    label: `#${item.id} - ${item.project_name || 'Chi tiết dự án'}`,
    active: true,
  })
  syncDetailQuery()
}

const setDetailTab = () => {
  const parsedId = Number(detailId.value || 0)
  if (!Number.isFinite(parsedId) || parsedId <= 0) return

  store.newTab({
    id: parsedId,
    label: `#${parsedId} - Chi tiết dự án`,
    active: true,
  })
}

const requestFetchList = _.debounce(async () => {
  try {
    await store.fetchList()
  } catch (e) {
    console.error('Không thể tải danh sách dự án', e)
  }
}, 350)

watch(
  () => store.uuid,
  () => requestFetchList(),
)

onMounted(async () => {
  store.changeTab('list')
  if (store.action_type === EstateProjectListActionTypes.PENDING) {
    await requestFetchList()
  }
  if (detailId.value) {
    setDetailTab()
    syncDetailQuery()
  }
})
</script>

<template>
  <DashboardLayout
    currentRoute="property/projects"
    page-title="Dự án bất động sản"
    :tabs="pageTabs"
    :tabsEvent="{
      close: handleCloseTab,
      select: handleChangeTab,
      closeAll: handleCloseAllTab,
    }"
  >
    <template v-if="displayTabContent === 'list'">
      <div class="table-responsive estate-project-list-create-card">
        <div class="table-heading flex justify-end">
          <AllowPerm :perm="['real_estate_project.create', 'real_estate.edit']" action="hide">
            <button type="button" class="table-btn-action estate-project-create-btn" @click="router.push({ name: 'property-projects-create' })">
              <Icon icon="stash:plus-light" height="20" />
              Thêm dự án mới
            </button>
          </AllowPerm>
        </div>
      </div>

      <DataTable
        :loading="loading"
        :tableConfig="tableConfig"
        :page-size="pageSize"
        :page="page"
        :total-page="dataSource?.totalPage || 0"
        :total-records="dataSource?.totalRecords || 0"
        :data="dataSource?.list || []"
        :row-action-buttons="[
          {
            action: (_e, item) => {
              handleOpenDetailTab(item)
            },
            css: 'btn btn-cs',
            label: 'Chi tiết',
            icon: 'iconamoon:edit-duotone',
            perm: ['real_estate.list', 'real_estate.edit'],
          },
        ]"
        :buttons="[]"
        primaryField="id"
        @page-changed="store.changePage"
        @pagesize-changed="store.changePageSize"
        @toggle-visibility="store.toggleColumnVisibility"
      />
    </template>

    <template v-else>
      <template v-for="tab in pageTabs" :key="String(tab.id)">
        <template v-if="tab.id === displayTabContent">
          <EstateProjectDetail :id="Number(tab.id)" />
        </template>
      </template>
    </template>
  </DashboardLayout>
</template>

<style scoped>


:deep(td.estate-project-cell-bold .value) {
  font-weight: 700;
}

.estate-project-list-create-card {
  margin-bottom: 32px;
  padding-bottom: 0px;
}

.estate-project-create-btn {
  background: #0059ff;
  color: #fff;
  border-radius: 8px;
  padding: 7px 16px;
  font-size: 10px;
  gap: 10px;
  height: 38px;
}

.estate-project-create-btn:hover {
  background: #0049d4;
}
</style>
